/**
 * Template validation utilities for handling Jinja template variables and validation
 */

/**
 * Extract variables from a Jinja template using regex pattern matching
 * @param template - The template string to extract variables from
 * @returns Array of unique variable names found in the template
 */
export const extractVariablesFromPrompt = (template: string): string[] => {
  const regex = /\{\{\s*([\w.]+)\s*\}\}/g;
  const matches = Array.from(template.matchAll(regex)).map(m => m[1]);
  return Array.from(new Set(matches));
};

/**
 * Create a nested object from dot notation key
 * @param key - The dot notation key (e.g., "agent.name")
 * @param value - The value to assign to the final key
 * @returns Nested object structure
 *
 * Example: createNestedObject("agent.name", "MyAgent")
 * Returns: { agent: { name: "MyAgent" } }
 */
export const createNestedObject = (key: string, value: any): Record<string, any> => {
  const keys = key.split(".");
  const result: Record<string, any> = {};
  let current = result;

  for (let i = 0; i < keys.length - 1; i++) {
    current[keys[i]] = {};
    current = current[keys[i]];
  }

  current[keys[keys.length - 1]] = value;
  return result;
};

/**
 * Merge nested objects, handling deep merging for nested structures
 * @param obj1 - First object to merge
 * @param obj2 - Second object to merge
 * @returns Merged object with nested structures preserved
 */
export const mergeNestedObjects = (obj1: Record<string, any>, obj2: Record<string, any>): Record<string, any> => {
  const result = { ...obj1 };

  for (const key in obj2) {
    if (obj2[key] && typeof obj2[key] === "object" && !Array.isArray(obj2[key])) {
      result[key] = mergeNestedObjects(result[key] || {}, obj2[key]);
    } else {
      result[key] = obj2[key];
    }
  }

  return result;
};

/**
 * Parse variable value, attempting to parse as JSON if it looks like JSON
 * @param value - The string value to parse
 * @returns Parsed value (JSON object/array) or original string
 */
export const parseVariableValue = (value: string): any => {
  try {
    if (value.startsWith("{") || value.startsWith("[") || value.startsWith('"')) {
      return JSON.parse(value);
    }
    return value;
  } catch {
    // If JSON parsing fails, keep as string
    return value;
  }
};

/**
 * Convert variable values to sample data structure, handling nested variables
 * @param variableValues - Record of variable names to their string values
 * @returns Sample data object with proper nesting for API consumption
 */
export const convertVariablesToSampleData = (variableValues: Record<string, string>): Record<string, any> => {
  let sampleData: Record<string, any> = {};

  // Separate nested and simple variables
  const nestedVariables: [string, string][] = [];
  const simpleVariables: [string, string][] = [];

  Object.entries(variableValues).forEach(([key, value]) => {
    if (key.includes(".")) {
      nestedVariables.push([key, value]);
    } else {
      simpleVariables.push([key, value]);
    }
  });

  // First, process all nested variables to build the nested structure
  nestedVariables.forEach(([key, value]) => {
    const parsedValue = parseVariableValue(value);
    const nestedObject = createNestedObject(key, parsedValue);
    sampleData = mergeNestedObjects(sampleData, nestedObject);
  });

  // Then, process simple variables, but only if they don't conflict with existing nested structure
  simpleVariables.forEach(([key, value]) => {
    const parsedValue = parseVariableValue(value);

    // Check if this key already exists as a nested object
    if (sampleData[key] && typeof sampleData[key] === "object" && !Array.isArray(sampleData[key])) {
      // If it's already a nested object, we can't override it with a simple value
      // This is a conflict - we'll skip this variable and log a warning
      console.warn(`Variable '${key}' conflicts with nested structure. Skipping simple value assignment.`);
    } else {
      sampleData[key] = parsedValue;
    }
  });

  return sampleData;
};

/**
 * Format validation result for display
 * @param result - API validation result
 * @param template - Original template (used as fallback when no preview)
 * @returns Formatted display result
 */
export const formatValidationResult = (
  result: any,
  template: string
): { displayResult: string; isSuccess: boolean } => {
  if (result.validation_passed) {
    // Show preview if available, otherwise show template as fallback
    const displayResult = result.preview || template || "Template validated successfully";
    return { displayResult, isSuccess: true };
  } else {
    // Show validation errors and preview if available
    const errorMessages =
      result.errors && result.errors.length > 0 ? result.errors.join("\n") : "Template validation failed";

    const displayResult = result.preview ? `❌ ${errorMessages}\n\nPreview:\n${result.preview}` : `❌ ${errorMessages}`;

    return { displayResult, isSuccess: false };
  }
};

/**
 * Check if a template has variables
 * @param template - The template string to check
 * @returns True if template contains variables, false otherwise
 */
export const hasTemplateVariables = (template: string): boolean => {
  return extractVariablesFromPrompt(template).length > 0;
};
